import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent, EVENT_TYPE_PAGE, trackGA4Event } from '../utils/trackEvent.jsx';
import { Button } from '@/components/ui/button';
import { CheckCircle2, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { CDFI_LENDERS_CONTENT } from '../config/cdfi-lenders.config.jsx';

// Define slides outside component to avoid dependency issues
const slides = [
  {
    id: 0,
    image: '/uploads/lender-page.png',
    content: {
      headline: CDFI_LENDERS_CONTENT.headline,
      description: CDFI_LENDERS_CONTENT.description,
      items: CDFI_LENDERS_CONTENT.benefits
    }
  },
  {
    id: 1,
    image: '/uploads/lendger-2.png',
    content: {
      headline: CDFI_LENDERS_CONTENT.headline,
      description: CDFI_LENDERS_CONTENT.description,
      items: CDFI_LENDERS_CONTENT.benefits
    }
  }
];

function CDFILenders() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    trackEvent(EVENT_TYPE_PAGE);
  }, []);

  // Auto-advance carousel every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleBecomePartner = () => {
    trackGA4Event('become_cdfi_partner_clicked', {
      button_location: 'cdfi_lenders_page',
      button_text: 'Become a CDFI Partner'
    });
    navigate('/partnerwithus');
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Split Layout: Image Carousel Left, Text Right */}
      <section className="relative overflow-hidden min-h-screen" aria-label="Hero section">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
          {/* Left Side - Image Carousel */}
          <div className="relative h-[50vh] lg:h-screen order-1 lg:order-1 bg-gray-100">
            <div className="relative h-full overflow-hidden">
              {/* All Slide Images with Fade Transition */}
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                    index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                  }`}
                  style={{ 
                    backgroundImage: `url("${slide.image}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
              ))}
              {/* Subtle overlay to ensure image doesn't compete with text */}
              <div className="absolute inset-0 bg-black/5 z-0" />
              
              {/* Carousel Navigation Arrows */}
              {slides.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-[#1a4a1a] p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                    aria-label="Previous slide"
                  >
                    <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-[#1a4a1a] p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                    aria-label="Next slide"
                  >
                    <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
                  </button>
                </>
              )}

              {/* Carousel Indicators */}
              {slides.length > 1 && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        currentSlide === index
                          ? 'bg-white w-8'
                          : 'bg-white/50 hover:bg-white/75 w-2'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Text Content - Light, inviting palette for CDFI leaders */}
          <div className="bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center py-12 md:py-16 lg:py-24 px-6 sm:px-8 lg:px-12 xl:px-16 order-2 lg:order-2">
            <div className="w-full max-w-2xl mx-auto space-y-8 md:space-y-10">
              {/* Headline */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-[#1a4a1a] leading-tight tracking-tight">
                {slides[currentSlide].content.headline}
              </h1>
              
              {/* Description */}
              <p className="text-lg md:text-xl lg:text-2xl text-emerald-900/90 leading-relaxed font-normal">
                {slides[currentSlide].content.description}
              </p>

              {/* Benefits List */}
              <div className="space-y-4 md:space-y-5 pt-2">
                {slides[currentSlide].content.items.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 md:gap-4">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-emerald-600 flex items-center justify-center shadow-md">
                        <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-white" strokeWidth={2.5} />
                      </div>
                    </div>
                    <p className="text-base md:text-lg lg:text-xl text-emerald-900 leading-relaxed font-medium pt-0.5">
                      {item}
                    </p>
                  </div>
                ))}
              </div>

              {/* CTA to Signup */}
              <div className="pt-4 md:pt-6 flex justify-center">
                <Button
                  onClick={() => navigate('/partner-signup')}
                  size="lg"
                  className="group bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg font-bold shadow-xl hover:shadow-[0_8px_30px_rgba(16,185,129,0.3)] hover:scale-105 transition-all duration-300 rounded-xl"
                >
                  Sign Up Now
                  <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-emerald-50/50" aria-label="Why this matters">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a4a1a] mb-6 leading-tight">
              {CDFI_LENDERS_CONTENT.whyItMattersTitle}
            </h2>
            <p className="text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed font-medium">
              {CDFI_LENDERS_CONTENT.whyItMattersDescription}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10 lg:p-12 border border-emerald-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {CDFI_LENDERS_CONTENT.whyItMattersPoints.map((point, index) => (
                <div key={index} className="flex items-start gap-4 group hover:bg-emerald-50/50 p-4 rounded-lg transition-colors">
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle2 className="w-6 h-6 md:w-7 md:h-7 text-emerald-600 group-hover:text-emerald-700 transition-colors" />
                  </div>
                  <p className="text-base md:text-lg text-gray-700 leading-relaxed group-hover:text-gray-900 transition-colors font-medium">
                    {point}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HalalCred Packages Section */}
      <section className="py-16 md:py-24 bg-white" aria-label="HalalCred packages">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a4a1a] mb-6 leading-tight">
              {CDFI_LENDERS_CONTENT.halalcredPackagesTitle}
            </h2>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 via-teal-50/80 to-emerald-50 rounded-2xl shadow-xl p-8 md:p-10 lg:p-12 border border-emerald-100 mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {CDFI_LENDERS_CONTENT.halalcredPackages.map((item, index) => (
                <div key={index} className="flex items-start gap-4 group hover:bg-white/50 p-4 rounded-lg transition-colors">
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle2 className="w-6 h-6 md:w-7 md:h-7 text-emerald-600 group-hover:text-emerald-700 transition-colors" />
                  </div>
                  <p className="text-base md:text-lg text-gray-700 leading-relaxed group-hover:text-gray-900 transition-colors font-medium">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <Button
              onClick={handleBecomePartner}
              size="lg"
              className="group bg-emerald-600 hover:bg-emerald-700 text-white px-10 py-6 text-lg font-bold shadow-2xl hover:shadow-[0_8px_30px_rgba(26,74,26,0.3)] hover:scale-105 transition-all duration-300 rounded-xl"
            >
              {CDFI_LENDERS_CONTENT.ctaButtonText}
              <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </section>

      {/* Fade-in Animation Styles */}
      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
      `}</style>
    </div>
  );
}

export default CDFILenders;

